import { setSentryUser } from './instrument';
import { useEffect } from 'react';
import { ReloadPrompt } from './components';
import { Layout } from 'components/layout';
import { Outlet, useLocation } from 'react-router-dom';
import { Iframe, iframePaths } from 'features/iframe';
import { usePageView } from 'lib/segment';
import { LoginContext, useUser } from 'features/auth';
import { AccessControl, RouterFallback } from 'features/accessControl';
import { SEGMENT_ANALYTICS_KEY, SEGMENT_ENV, SEGMENT_COOKIE_DOMAIN } from 'config';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createMockAnalytics } from 'lib/segment/mockAnalytics';
import { loadHubSpot } from 'lib/hubspot';

const IS_MOCK_ANALYTICS = SEGMENT_ANALYTICS_KEY === 'mock';
export const analytics = IS_MOCK_ANALYTICS ? createMockAnalytics() : new AnalyticsBrowser();

if (SEGMENT_ANALYTICS_KEY) {
  analytics.load(
    {
      writeKey: SEGMENT_ANALYTICS_KEY,
      cdnURL: 'https://cdn.segment.com',
    },
    {
      cookie: {
        domain: SEGMENT_COOKIE_DOMAIN,
      },
    }
  );
}

loadHubSpot();

export const identify = (u: LoginContext) => {
  if (SEGMENT_ANALYTICS_KEY && u) {
    analytics.identify(`${SEGMENT_ENV}:${u.id}`, {
      name: u.name,
      email: u.email,
      createdAt: u.signUpDate,
      roles: u.roleNames,
      environment: SEGMENT_ENV,
      loggedInUserId: u.id,
      loggedInUserOrganizationId: String(u.organizationId),
      loggedInUserOrganizationType: u.organizationType === 1 ? 'Designer' : 'Supplier',
    });

    // Group user with their organization
    analytics.group(`${SEGMENT_ENV}:${u.organizationId.toString()}`, {
      name: u.organizationName,
      type: u.organizationType === 1 ? 'Designer' : 'Supplier',
      plan: u.planType,
      plan_tier: u.planTier,
      environment: SEGMENT_ENV,
      loggedInUserId: u.id,
      loggedInUserOrganizationId: String(u.organizationId),
    });
  }

  // Set Sentry user to match analytics identify
  setSentryUser(u);
};

export const App = () => {
  const location = useLocation();
  const { data } = useUser();

  useEffect(() => {
    if (data?.loginContext) {
      identify(data.loginContext);
    }
  }, [data]);

  // Register navigation to segment in an SPA
  usePageView(Boolean(SEGMENT_ANALYTICS_KEY));

  // Set app version for Segment
  window.app_version = APP_VERSION;

  // iframe
  const showIframe =
    iframePaths.includes(location.pathname) ||
    iframePaths.some(p => location.pathname.toLowerCase().includes(p));

  return (
    <Layout iframe={showIframe}>
      <AccessControl fallback={<RouterFallback />}>
        <Outlet />
        <Iframe path={location.pathname} />
        <ReloadPrompt />
      </AccessControl>
    </Layout>
  );
};
